import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import ProjectsPage from './components/ProjectsPage';
import ResumePage from './components/ResumePage';
import ChatPage from './components/ChatPage';
import ProjectsAdmin from './components/admin/ProjectsAdmin';
import AdminLogin from './components/admin/AdminLogin';
import { FirebaseProvider, useFirebase } from './contexts/FirebaseContext';
import { IRCProvider } from './contexts/IRCContext';

const Navigation: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { currentUser, loading, signOut } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminPage = location.pathname === '/admin';

  const handleAdminToggle = () => {
    if (isAdmin) {
      setIsAdmin(false);
      navigate('/');
    } else {
      setIsAdmin(true);
      navigate('/admin');
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Close mobile menu when changing pages
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav className="shadow py-4 w-full">
      <div className="flex items-center justify-between w-full px-0">
        <div className="flex items-center">
          <Link to="/" className="nav-brand text-xl font-bold pl-6">
            <h3>🐧 Daniel du Pré</h3>
          </Link>
          
          <div className={`nav-links ${menuOpen ? 'active' : ''}`}>
            <Link to="/projects" className="pl-2">
              Projects
            </Link>
            <Link to="/resume" className="pl-2">
              Resume
            </Link>
            <Link to="/chat" className="pl-2">
              Chat
            </Link>
          </div>
        </div>
        
        <div className="pr-20 flex items-center">
          {currentUser ? (
            <div className="dropdown relative">
              <button className="dropdown-toggle has-menu focus:outline-none" tabIndex={0}>
                <span>{currentUser.email}</span>
              </button>
              <div className="dropdown-menu">
                <button
                  onClick={handleAdminToggle}
                  className="text-blue-600"
                >
                  Settings
                </button>
                <button
                  onClick={signOut}
                  className="text-red-600"
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <Link
              to="/admin"
              className="text-white"
            >
              Sign In
            </Link>
          )}
          
          <button className="hamburger-menu ml-4" onClick={toggleMenu} aria-label="Toggle menu">
            ☰
          </button>
        </div>
      </div>
    </nav>
  );
};

const AppContent: React.FC = () => {
  const { currentUser, loading } = useFirebase();
  const location = useLocation();
  const navigate = useNavigate();

  // Check for redirect path in session storage or URL on component mount
  useEffect(() => {
    // Check for session storage redirect
    const redirectPath = sessionStorage.getItem('redirect');
    if (redirectPath) {
      // Clear the redirect from session storage
      sessionStorage.removeItem('redirect');
      // Navigate to the stored path
      navigate(redirectPath);
      return;
    }

    // Check for URL query parameter redirect (from 404.html)
    const params = new URLSearchParams(window.location.search);
    const redirectParam = params.get('redirect');
    if (redirectParam) {
      // Navigate to the path from the query parameter
      navigate(redirectParam);
      // Clean up the URL by removing the query parameter
      window.history.replaceState(null, '', redirectParam);
    }
  }, [navigate]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="App min-h-screen bg-gray-50">
      <Navigation />
      
      <Routes>
        <Route path="/" element={<ProjectsPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/resume" element={<ResumePage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route 
          path="/admin" 
          element={currentUser ? <ProjectsAdmin /> : <AdminLogin onLoginSuccess={() => {}} />} 
        />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <FirebaseProvider>
      <IRCProvider>
        <AppContent />
      </IRCProvider>
    </FirebaseProvider>
  );
}

export default App;
